
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { formatAmount, getFeatures } from "../utils/formatters";

interface Plan {
  id: string;
  product: {
    id: string;
    name: string;
    description: string;
    metadata: {
      features?: string;
    };
  };
  unit_amount: number;
  currency: string;
  recurring: {
    interval: string;
    interval_count: number;
  };
}

interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  cancel_at_period_end: boolean;
  plan: {
    nickname: string;
    amount: number;
    currency: string;
    interval: string;
  };
}

interface AvailablePlansProps {
  plans: Plan[];
  subscription: Subscription | null;
  processingPlan: string | null;
  onSubscribe: (priceId: string) => Promise<void>;
}

export default function AvailablePlans({
  plans,
  subscription,
  processingPlan,
  onSubscribe
}: AvailablePlansProps) {
  return (
    <>
      <h2 className="text-xl font-semibold mb-4">{subscription ? 'Available Plans' : 'Choose a Plan'}</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.length > 0 ? (
          plans.map((plan) => (
            <Card key={plan.id} className="flex flex-col h-full">
              <CardHeader>
                <CardTitle>{plan.product.name}</CardTitle>
                <CardDescription>{plan.product.description}</CardDescription>
                <div className="mt-2 text-2xl font-bold">
                  {formatAmount(plan.unit_amount, plan.currency)}
                  <span className="text-sm font-normal text-muted-foreground">
                    /{plan.recurring.interval}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-2">
                  {getFeatures(plan).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  className="w-full"
                  onClick={() => onSubscribe(plan.id)}
                  disabled={
                    processingPlan === plan.id || 
                    (subscription && subscription.plan.nickname === plan.product.name)
                  }
                >
                  {processingPlan === plan.id ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : subscription && subscription.plan.nickname === plan.product.name ? (
                    <CheckCircle className="mr-2 h-4 w-4" />
                  ) : null}
                  {subscription && subscription.plan.nickname === plan.product.name
                    ? 'Current Plan'
                    : 'Subscribe'}
                </Button>
              </CardFooter>
            </Card>
          ))
        ) : (
          <Card className="col-span-full">
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertCircle className="mr-2 h-5 w-5 text-amber-500" />
                No Plans Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>No subscription plans are currently available. Please check back later.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
